import * as React from 'react';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {EditIcon, TrashIcon} from 'shells/icon';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {UserCreateInput} from 'nutshell-graphql-types';

import {useGetUsersByFilter, useCreateUser} from '../graphql/hooks';
import {useGetRoles, useUpdateRole, useDeleteRole} from '../roles-permissions/graphql/hooks';

import {CreateUserModal} from './create-user-modal';
import {UsersTable} from './users-table';

type Props = {
    id: string;
};

export function RoleTableWrapper(props: Props) {
    const [isRenamingRole, setIsRenamingRole] = React.useState<boolean>(false);
    const [isConfirmingDeleteRole, setIsConfirmingDeleteRole] = React.useState<boolean>(false);
    const [isCreatingUser, setIsCreatingUser] = React.useState<boolean>(false);
    const [createUserError, setCreateUserError] = React.useState<string | null>(null);

    const roleId = `${props.id}-roles`;
    const {roles} = useGetRoles();
    const activeRole = roles && roles.find((role) => role.id === roleId);

    const {
        users,
        isLoading,
        errorMessage: usersErrorMessage,
        fetchMoreRows,
        hasNextPage,
        totalCount,
        endCursor,
        refetch: refetchUsers,
    } = useGetUsersByFilter({roleId, statusFilter: 0});
    const {fetchMoreRows: fetchMoreEnabledUsers} = useGetUsersByFilter({statusFilter: 0});
    const {updateRole, isUpdating} = useUpdateRole();
    const {deleteRole, isDeleting, errorMessage: deleteRoleErrorMessage} = useDeleteRole();
    const {createUser, isCreating, error, existingUserId} = useCreateUser();

    const onUpdateRole = (name: string) => {
        updateRole({roleId, name}).then(() => setIsRenamingRole(false));
    };

    const onCreateUser = (input: UserCreateInput) => {
        setCreateUserError(null);
        createUser(input).then((result) => {
            if (
                result &&
                result.data &&
                result.data.userCreate &&
                result.data.userCreate.isExistingUser
            ) {
                return;
            }

            fetchMoreRows();
            fetchMoreEnabledUsers();

            // Timeout allows the success animation to play before closing the modal
            setTimeout(() => {
                setIsCreatingUser(false);
                window.location.reload();
            }, 1000);
        });
    };

    React.useEffect(() => {
        if (error) {
            setCreateUserError(error);
        }
    }, [error]);

    const onDeleteRole = () => {
        deleteRole({roleId}).then(() => setIsConfirmingDeleteRole(false));
    };

    // Refetch users when the table loads after its first load so we show any new users that were added
    React.useEffect(() => {
        if (endCursor) {
            fetchMoreRows();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleId]);

    const actionMenuOptions =
        activeRole && !activeRole.isImmutable
            ? [
                  {
                      label: 'Rename',
                      icon: <EditIcon size={15} />,
                      handleSelect: () => setIsRenamingRole(true),
                  },
                  {
                      label: 'Delete',
                      icon: <TrashIcon size={15} />,
                      handleSelect: () => setIsConfirmingDeleteRole(true),
                      variant: 'destructive',
                  },
              ]
            : undefined;

    return (
        <>
            <UsersTable
                title={`${activeRole ? activeRole.name : 'Enabled'} users`}
                users={users}
                isLoading={isLoading}
                errorMessage={usersErrorMessage}
                fetchMore={fetchMoreRows}
                hasNextPage={hasNextPage}
                totalCount={totalCount}
                emptyStateMessage='There are no users in this role'
                // @ts-expect-error
                actionMenuOptions={actionMenuOptions}
                onAddRowClick={() => setIsCreatingUser(true)}
                refetchUsers={refetchUsers}
            />
            {isCreatingUser && (
                <CreateUserModal
                    onSubmit={onCreateUser}
                    onClose={() => {
                        setIsCreatingUser(false);
                        setCreateUserError(null);
                    }}
                    isSaving={isCreating}
                    errorMessage={createUserError}
                    existingUserId={existingUserId}
                />
            )}
            {isRenamingRole && (
                <SingleInputModal
                    initialValue={activeRole ? activeRole.name : ''}
                    requireValue={true}
                    buttonText='Save'
                    title='Rename this role'
                    isSaving={isUpdating}
                    onSave={onUpdateRole}
                    onClose={() => setIsRenamingRole(false)}
                />
            )}
            {isConfirmingDeleteRole && (
                <ConfirmationDialog
                    title='Delete role'
                    message='Are you sure you want to delete this role?'
                    confirmText='Yes, delete'
                    cancelText='No, don’t delete'
                    isConfirmDestructive={true}
                    onCancel={() => {
                        setIsConfirmingDeleteRole(false);
                    }}
                    onConfirm={onDeleteRole}
                    isSaving={isDeleting}
                    // @ts-expect-error
                    errorMessage={deleteRoleErrorMessage}
                />
            )}
        </>
    );
}
